import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from '~/utils/navigation';
import styled from 'styled-components';
import { Input } from '@comeon/ui';
import CheckboxDropdown from './checkbox-dropdown.js';
import sowOptions from './sow-options.js';
import Icon, { ICON_UPLOAD_LINE, ICON_CHEVRON_UP } from '~/components/icon/index.js';
import ButtonWrapper, { SimpleButtonWrapper } from '~/components/misc/button-wrapper.js';
import { HeadWrapper, MainTitle, SubTitle } from '~/components/misc/section-head.js';
import { Sidebar, SidebarContent, SidebarHeader } from '~/components/sidebar/index.js';
import Loading from '../../misc/loading.js';

import ContactSupportAgent from '~/components/support/contact-support-agent.js';

import useTranslations from '~/components/translation/use-translations.ts';
import useContextualPathTransformer from '~/hooks/use-contextual-path-transformer.js';
import { ROUTE_ACCOUNT } from '~/components/router/constants.js';

import { showNotification } from '~/components/notifications/ducks/notifications.js';
import {
	getPlayerSOWStatus,
	resetSOW,
	sowSelectorPlayerStatus,
	sowSelectorUploadStatus,
	sowSelectorUploadError,
	uploadFiles,
	resetUploadFileStatus
} from './ducks/sow.js';

import { selectPlayerFullName } from '../ducks/index.js';

import {
	sowPageLoad,
	sowOptionsSelect,
	sowAttachedFiles,
	sowSuccessfulUploads,
	sowErrorUploads
} from '@comeon/analytics';

import IconButtonClose from '../../misc/icon-button-close.js';
import { sendAppBridgeEvent, WEB_EVENTS } from '~/components/app-bridge';

const DropdownButton = styled.button`
	height: 50px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0px 10px;
	border-radius: 3px 3px 0px 0px;
	background-color: rgba(var(--source-of-wealth-form-bg), 1);
	color: rgb(var(--input-placeholder-color));
	color: ${props =>
		props.hasValue ? 'rgb(var(--source-of-wealth-form-text))' : 'rgb(var(--input-placeholder-color))'};
	font-size: 16px;
	font-weight: 700;
	font: var(--heading-5);
	height: 56px;
	border-bottom: 1px solid rgb(var(--input-border-color));

	&:hover {
		border-bottom-color: rgb(var(--input-border-hover-color));
	}
`;

const getFormattedDateTime = () => {
	const options = {
		weekday: 'long',
		year: 'numeric',
		month: 'long',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hour12: false
	};
	const formattedDate = `${new Date().toLocaleTimeString('en-us', options)}`;
	return formattedDate;
};

const acceptedFileTypes = ['image/jpg', 'image/jpeg', 'image/png', 'application/pdf'];

const textKeys = [
	'SOW_PAGE_HEADER',
	'SOW_PAGE_DESCRIPTION',
	'SOW_PAGE_DISABLED_TEXT',
	'SOW_UPLOAD_SUCCESSFUL',
	'SOW_UPLOAD_ERROR',
	'SOW_ADD_FILES_BUTTON_TEXT',
	'SOW_UPLOAD_HELPTEXT1',
	'SOW_UPLOAD_HELPTEXT2',
	'SOW_UPLOAD_HELPTEXT3',
	'SOW_EDIT_HELPTEXT',
	'SOW_SUBMISSION',
	'SOW_SUBMITTING',
	'SOW_STILL_SUBMITTING',
	'SOW_PLACEHOLDER_TEXT',
	'SOW_MAXIMUM_NO_OF_FILES',
	'SOW_EMPLOYER_LABEL',
	'SOW_OCCUPATION_LABEL',
	'SOW_FULL_NAME_LABEL',
	'SOW_EMPLOYER_PLACEHOLDER',
	'SOW_OCCUPATION_PLACEHOLDER',
	'SOW_UPLOAD_FAILED_FILE_NOT_SUPPORTED',
	'SOW_UPLOAD_FAILED_FILE_TOO_LARGE',
	'SOW_MAXIMUM_NO_OF_FILES'
];

const SourceOfWealth = ({ onClose, setCurrentSOWFormState }) => {
	const dispatch = useDispatch();
	const router = useRouter();
	const transformPath = useContextualPathTransformer();

	const [currentSelected, setCurrentSelected] = useState([]);
	const [sowData, setSowData] = useState(null);
	const [formFiles, setFormFiles] = useState([]);
	const [dropdownOpen, setDropdownOpen] = useState(false);

	const [dropdownPlaceHolderText, setDropdownPlaceHolderText] = useState('');

	const name = useSelector(selectPlayerFullName);
	const sowPlayerStatus = useSelector(sowSelectorPlayerStatus);
	const sowUploadStatus = useSelector(sowSelectorUploadStatus);
	const sowUploadError = useSelector(sowSelectorUploadError);

	const [occupation, setOccupation] = useState('');
	const [employer, setEmployer] = useState('');

	const [sowUploadTimer, setSowUploadTimer] = useState(null);
	const [longUpload, setLongUpload] = useState(false);

	const { translations, isLoadingTranslations } = useTranslations([
		...textKeys,
		...sowOptions.reduce((acc, curr) => {
			acc.push(curr.title);
			if (curr?.subtitle) {
				acc.push(curr.subtitle);
			}
			if (curr?.placeholder) {
				acc.push(curr.placeholder);
			}

			return acc;
		}, [])
	]);

	useEffect(() => {
		if (translations && translations.SOW_PLACEHOLDER_TEXT) {
			setDropdownPlaceHolderText(translations.SOW_PLACEHOLDER_TEXT);
		}
	}, [translations]);

	// Get player status when Page loads
	useEffect(() => {
		dispatch(getPlayerSOWStatus());
		// track page load
		sowPageLoad();
		return () => {
			// reset everything in SOW when the page is closed
			dispatch(resetSOW());
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (sowUploadStatus === 'success') {
			// track successfull upload of docs
			sowSuccessfulUploads(currentSelected);
			// set the current state
			setCurrentSOWFormState('upload-success');
			dispatch(
				showNotification({
					color: 'green',
					autoHide: true,
					autoHideDuration: 10000,
					message: translations.SOW_UPLOAD_SUCCESSFUL
				})
			);
			// Send notification to apps to show the success message
			sendAppBridgeEvent(WEB_EVENTS.SOW_UPLOAD_SUCCESSFUL);
			dispatch(resetSOW());
			setFormFiles('');
			setSowData(null);
			setCurrentSelected([]);
			clearTimeout(sowUploadTimer);
			setSowUploadTimer(null);
			setLongUpload(false);
			// redirect to Account menu when successfully uploaded
			router.push(transformPath(ROUTE_ACCOUNT));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sowUploadStatus]);

	useEffect(() => {
		if (sowUploadStatus === 'loading' && !sowUploadTimer) {
			const timerId = setTimeout(() => {
				setLongUpload(true);
			}, 5000);

			setSowUploadTimer(timerId);
		} else if (sowUploadStatus !== 'loading' && sowUploadTimer) {
			clearTimeout(sowUploadTimer);
			setSowUploadTimer(null);
			setLongUpload(false);
		}
	}, [sowUploadStatus, sowUploadTimer]);

	useEffect(() => {
		if (sowUploadError) {
			// set the current state
			setCurrentSOWFormState('pageload');

			// track error in upload
			sowErrorUploads(sowUploadError.status);

			if (sowUploadError.status === 413) {
				dispatch(
					showNotification({
						color: 'red',
						autoHide: true,
						autoHideDuration: 10000,
						message: translations.SOW_UPLOAD_FAILED_FILE_TOO_LARGE
					})
				);
			} else if (sowUploadError.status === 415) {
				dispatch(
					showNotification({
						color: 'red',
						autoHide: true,
						autoHideDuration: 10000,
						message: translations.SOW_UPLOAD_FAILED_FILE_NOT_SUPPORTED
					})
				);
			} else {
				dispatch(
					showNotification({
						color: 'red',
						autoHide: true,
						autoHideDuration: 10000,
						message: translations.SOW_UPLOAD_ERROR
					})
				);
			}

			dispatch(resetUploadFileStatus());
			setFormFiles('');
			setSowData(null);
			setCurrentSelected([]);
			clearTimeout(sowUploadTimer);
			setSowUploadTimer(null);
			setLongUpload(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sowUploadError]);

	const handleImageAdded = e => {
		// set the current state
		setCurrentSOWFormState('files-attached');
		e.preventDefault();
		const formData = new FormData();

		const fileArray = Array.from(e.target.files);
		if (fileArray.length <= 15) {
			let isFileSizeBig = false;
			let isFileTypeNotAllowed = false;
			const fileNames = [];
			fileArray.forEach(item => {
				const fileExtention = item.name.match(/\.([^.]+)$/)[1];
				// console.log('fileExtention', fileExtention);
				if (item.size <= 10 * 1024 * 1024) {
					if ([`png`, `jpg`, `jpeg`, `pdf`].includes(fileExtention)) {
						fileNames.push(item.name);
						formData.append('uploadFiles', item);
					} else {
						isFileTypeNotAllowed = true;
					}
				} else {
					isFileSizeBig = true;
				}
			});

			if (isFileSizeBig) {
				dispatch(
					showNotification({
						color: 'red',
						autoHide: true,
						autoHideDuration: 10000,
						message: translations.SOW_UPLOAD_FAILED_FILE_TOO_LARGE
					})
				);
			} else if (isFileTypeNotAllowed) {
				dispatch(
					showNotification({
						color: 'red',
						autoHide: true,
						autoHideDuration: 10000,
						message: translations.SOW_UPLOAD_FAILED_FILE_NOT_SUPPORTED
					})
				);
			}

			setSowData(formData);
			setFormFiles(fileNames);
			sowAttachedFiles(fileNames.length);
		} else {
			e.preventDefault();
			dispatch(
				showNotification({
					color: 'red',
					autoHide: true,
					autoHideDuration: 10000,
					message: translations.SOW_MAXIMUM_NO_OF_FILES
				})
			);
		}
	};

	const handleUploadFiles = e => {
		// set the current state
		setCurrentSOWFormState('upload-called');
		const finalSowFormData = sowData;
		finalSowFormData.append(
			'docForm',
			new Blob(
				[
					JSON.stringify({
						sow: currentSelected,
						occupation,
						employer
					})
				],
				{
					type: 'application/json'
				}
			)
		);

		setSowData(finalSowFormData);
		e.preventDefault();
		dispatch(uploadFiles(finalSowFormData));
	};

	const formattedOptions = sowOptions.reduce((accumulator, current) => {
		accumulator.push({
			value: current.value,
			title: translations[current.title],
			subtitle: translations[current?.subtitle],
			isChecked: currentSelected.includes(current.value),
			isCustom: current.isCustom,
			placeholder: current.placeholder ? translations[current.placeholder] : current.placeholder
		});
		return accumulator;
	}, []);

	if (isLoadingTranslations || sowPlayerStatus === `fetching`) {
		return <Loading />;
	}

	return (
		<Sidebar>
			<SidebarHeader
				className="sow-custom-header"
				Icon={
					<IconButtonClose
						data-at="flow-navigation-source-of-wealth-close"
						className="page-head__close"
						onClick={onClose}
					/>
				}
			>
				<div className="page-head__title" />
				<ContactSupportAgent
					chatTags={['sow']}
					className="page-head__right-side sow-contact-support-override"
				/>
			</SidebarHeader>

			<SidebarContent className="sow-section-wrapper">
				{[`1`, `2`, `3`, 1, 2, 3].includes(sowPlayerStatus) ? (
					<>
						<HeadWrapper padding="12px">
							<Icon type={ICON_UPLOAD_LINE} />
							<MainTitle dataAt="section-main-title-source-of-wealth">
								{translations.SOW_PAGE_HEADER}
							</MainTitle>
							<SubTitle dataAt="section-sub-title-source-of-wealth">
								{translations.SOW_PAGE_DESCRIPTION}
							</SubTitle>
						</HeadWrapper>
						<div className="u-pl1 u-pr1 u-mb1 u-flex u-align-vert">
							<Input
								label={translations.SOW_FULL_NAME_LABEL}
								value={name}
								type="text"
								id="sow-full-name"
								disabled
								className="sow-form-name"
							/>
						</div>{' '}
						<div className="u-pl1 u-pr1 u-mb1 u-flex u-align-vert">
							<Input
								active
								label={translations.SOW_OCCUPATION_LABEL}
								value={occupation}
								placeholder={translations.SOW_OCCUPATION_PLACEHOLDER}
								type="text"
								id="sow-occupation"
								className="sow-form-name"
								onChange={e => setOccupation(e.target.value)}
							/>
						</div>
						<div className="u-pl1 u-pr1 u-flex u-align-vert">
							<Input
								active
								label={translations.SOW_EMPLOYER_LABEL}
								value={employer}
								placeholder={translations.SOW_EMPLOYER_PLACEHOLDER}
								type="text"
								id="sow-employer"
								className="sow-form-name"
								onChange={e => setEmployer(e.target.value)}
							/>
						</div>
						{sowUploadStatus === 'loading' ? (
							<Loading />
						) : (
							<div className="u-relative sow-custom-dropdown">
								<SimpleButtonWrapper>
									<DropdownButton
										onClick={() => setDropdownOpen(!dropdownOpen)}
										hasValue={currentSelected.length > 0}
									>
										{dropdownPlaceHolderText}
										<Icon type={ICON_CHEVRON_UP} className="sow-chevron-icon" />
									</DropdownButton>
								</SimpleButtonWrapper>

								<div className="u-pl1 u-pr1 u-div-c">
									{formFiles.length > 0 && (
										<p className="sow-current-date">{getFormattedDateTime()}</p>
									)}
									<label
										htmlFor="source-of-wealth-upload"
										className={`sow-hidden-input ${
											currentSelected.length === 0 ? 'disabled' : 'not-disabled'
										} ${formFiles.length === 0 ? 'no-files-chosen' : ''}`}
										data-at="source-of-wealth-upload-files"
									>
										{formFiles.length > 0 ? (
											<>
												<div className="uploaded-file-names-wrapper">
													{formFiles.map(item => (
														<span key={item}>{`${item}, `}</span>
													))}
												</div>
												<div className="reviewing-text">Reviewing</div>
											</>
										) : (
											translations.SOW_ADD_FILES_BUTTON_TEXT
										)}
									</label>
									<input
										id="source-of-wealth-upload"
										name="source-of-wealth-upload"
										type="file"
										// className="upload"
										onChange={handleImageAdded}
										accept={`${acceptedFileTypes.join(', ')}`}
										disabled={currentSelected?.length === 0}
										multiple
									/>
									{formFiles.length === 0 ? (
										<>
											<p className="sow-upload-helptext">{translations.SOW_UPLOAD_HELPTEXT1}</p>
											<p className="sow-upload-helptext">{translations.SOW_UPLOAD_HELPTEXT2}</p>
											<p className="sow-upload-helptext">{translations.SOW_UPLOAD_HELPTEXT3}</p>
										</>
									) : (
										<p className="sow-upload-helptext">{translations.SOW_EDIT_HELPTEXT}</p>
									)}
								</div>
							</div>
						)}
					</>
				) : (
					<HeadWrapper padding="12px">
						<Icon type={ICON_UPLOAD_LINE} />
						<MainTitle dataAt="section-main-title-source-of-wealth">
							{translations.SOW_PAGE_DISABLED_TEXT}
						</MainTitle>
					</HeadWrapper>
				)}

				{dropdownOpen && (
					<CheckboxDropdown
						currentSelected={currentSelected}
						options={formattedOptions}
						setDropdownOpen={setDropdownOpen}
						dropdownPlaceHolderText={translations.SOW_PLACEHOLDER_TEXT}
						setDropdownText={setDropdownPlaceHolderText}
						onSubmit={updatedOptions => {
							setCurrentSOWFormState('options-selected');
							sowOptionsSelect(updatedOptions);
							setCurrentSelected(updatedOptions);
							if (sowData !== null) {
								const updatedFormData = sowData;
								updatedFormData.set(
									'docForm',
									new Blob(
										[
											JSON.stringify({
												sow: updatedOptions
											})
										],
										{
											type: 'application/json'
										}
									)
								);
								setSowData(updatedFormData);
							}
						}}
					/>
				)}
			</SidebarContent>
			<ButtonWrapper smallerPositionValue="sticky" biggerPositionValue="sticky" webkitSticky>
				{[`1`, `2`, `3`, 1, 2, 3].includes(sowPlayerStatus) && (
					<button
						type="button"
						className="btn"
						data-at="source-of-wealth-cta-upload-button"
						onClick={handleUploadFiles}
						disabled={
							formFiles.length === 0 ||
							sowUploadStatus === 'loading' ||
							occupation === '' ||
							employer === ''
						}
					>
						{sowUploadStatus === 'loading'
							? longUpload
								? translations.SOW_STILL_SUBMITTING
								: translations.SOW_SUBMITTING
							: translations.SOW_SUBMISSION}
					</button>
				)}
			</ButtonWrapper>
		</Sidebar>
	);
};

export default SourceOfWealth;
